/*
 * Coucke — Favoris (wishlist)
 *
 * Stockage 100% localStorage : marche sans compte client, donc pour tous les
 * visiteurs. Contrepartie assumée : la liste est attachée au navigateur.
 * On ne garde que les handles produit ; le prix, le stock et le visuel sont
 * re-rendus depuis le thème (`/products/<handle>?view=card`) pour qu'ils ne
 * soient jamais périmés.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'coucke:wishlist';
  var CHANGE_EVENT = 'wishlist:change';

  function read() {
    try {
      var parsed = JSON.parse(localStorage.getItem(STORAGE_KEY));
      if (!Array.isArray(parsed)) return [];
      return parsed.filter(function (handle) {
        return typeof handle === 'string' && handle.length > 0;
      });
    } catch (error) {
      return [];
    }
  }

  function write(handles) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(handles));
    } catch (error) {
      /* quota plein ou navigation privée : on continue en mémoire pour la page courante */
    }
    document.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { handles: handles } }));
  }

  var Wishlist = {
    all: read,
    count: function () {
      return read().length;
    },
    has: function (handle) {
      return read().indexOf(handle) !== -1;
    },
    add: function (handle) {
      var handles = read();
      if (handles.indexOf(handle) === -1) {
        handles.unshift(handle);
        write(handles);
      }
      return true;
    },
    remove: function (handle) {
      var handles = read().filter(function (item) {
        return item !== handle;
      });
      write(handles);
      return false;
    },
    toggle: function (handle) {
      return this.has(handle) ? this.remove(handle) : this.add(handle);
    }
  };

  window.CouckeWishlist = Wishlist;

  /* Klaviyo : n'est rattaché à un profil que si le visiteur est déjà identifié.
     Sur un visiteur anonyme l'appel est simplement ignoré, sans erreur. */
  function trackKlaviyo(dataset) {
    if (!window.klaviyo || typeof window.klaviyo.push !== 'function') return;
    try {
      window.klaviyo.push([
        'track',
        'Added to Wishlist',
        {
          ProductName: dataset.productTitle,
          ProductID: dataset.productId,
          Handle: dataset.productHandle,
          URL: window.location.origin + dataset.productUrl,
          ImageURL: dataset.productImage || '',
          Price: parseFloat(dataset.productPrice) || 0,
          Currency: dataset.productCurrency || ''
        }
      ]);
    } catch (error) {
      /* le tracking ne doit jamais casser l'interaction */
    }
  }

  /*
   * Pont vers les Événements client Shopify. Le thème n'a pas accès à gtag :
   * GA4 tourne dans le bac à sable des pixels. On publie donc un événement
   * custom, qu'un pixel personnalisé relaie vers GA4 (add_to_wishlist).
   */
  function publish(eventName, dataset) {
    var analytics = window.Shopify && window.Shopify.analytics;
    if (!analytics || typeof analytics.publish !== 'function') return;
    var payload = { wishlistSize: Wishlist.count() };
    /* wishlist_view n'a pas de produit : on n'envoie pas de champs vides */
    if (dataset.productHandle) {
      payload.handle = dataset.productHandle;
      payload.productId = dataset.productId;
      payload.title = dataset.productTitle;
      payload.price = parseFloat(dataset.productPrice) || 0;
      payload.currency = dataset.productCurrency || '';
    }
    try {
      analytics.publish(eventName, payload);
    } catch (error) {
      /* la mesure ne doit jamais casser l'interaction */
    }
  }

  class WishlistButton extends HTMLElement {
    connectedCallback() {
      this.handle = this.dataset.productHandle;
      this.button = this.querySelector('.wishlist-button__toggle');
      if (!this.handle || !this.button) return;

      this.onClick = this.onClick.bind(this);
      this.render = this.render.bind(this);
      this.button.addEventListener('click', this.onClick);
      document.addEventListener(CHANGE_EVENT, this.render);
      this.render();
    }

    disconnectedCallback() {
      document.removeEventListener(CHANGE_EVENT, this.render);
    }

    onClick(event) {
      /* la carte produit entière est un lien : ne pas naviguer */
      event.preventDefault();
      event.stopPropagation();
      var isActive = Wishlist.toggle(this.handle);
      if (isActive) trackKlaviyo(this.dataset);
      publish(isActive ? 'wishlist_add' : 'wishlist_remove', this.dataset);
    }

    render() {
      var isActive = Wishlist.has(this.handle);
      var label = isActive ? this.button.dataset.labelRemove : this.button.dataset.labelAdd;
      this.classList.toggle('is-active', isActive);
      this.button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      this.button.setAttribute('aria-label', label);
      var text = this.button.querySelector('.wishlist-button__label');
      if (text) text.textContent = label;
    }
  }
  customElements.define('wishlist-button', WishlistButton);

  /* Pastille de comptage dans le header */
  class WishlistCount extends HTMLElement {
    connectedCallback() {
      this.render = this.render.bind(this);
      document.addEventListener(CHANGE_EVENT, this.render);
      this.render();
    }

    disconnectedCallback() {
      document.removeEventListener(CHANGE_EVENT, this.render);
    }

    render() {
      var count = Wishlist.count();
      this.textContent = count > 0 ? count : '';
      this.hidden = count === 0;
    }
  }
  customElements.define('wishlist-count', WishlistCount);

  /* Page /pages/favoris : rend les vraies cartes produit du thème */
  class WishlistGrid extends HTMLElement {
    connectedCallback() {
      this.list = this.querySelector('[data-wishlist-list]');
      this.empty = this.querySelector('[data-wishlist-empty]');
      this.rootUrl = (this.dataset.rootUrl || '/').replace(/\/$/, '');
      this.rendered = [];

      this.onChange = this.onChange.bind(this);
      document.addEventListener(CHANGE_EVENT, this.onChange);
      publish('wishlist_view', {});
      this.load();
    }

    disconnectedCallback() {
      document.removeEventListener(CHANGE_EVENT, this.onChange);
    }

    /* Un retrait depuis la page elle-même : on enlève la carte, sans tout recharger */
    onChange() {
      var handles = Wishlist.all();
      this.rendered = this.rendered.filter(function (handle) {
        return handles.indexOf(handle) !== -1;
      });
      this.list.querySelectorAll('[data-wishlist-item]').forEach(function (item) {
        if (handles.indexOf(item.dataset.wishlistItem) === -1) item.remove();
      });
      /* un ajout fait depuis un autre onglet, ou un premier rendu vide */
      if (handles.length > this.rendered.length) this.load();
      else this.toggleEmpty(handles.length === 0);
    }

    toggleEmpty(isEmpty) {
      this.empty.hidden = !isEmpty;
      this.list.hidden = isEmpty;
      this.classList.remove('is-loading');
    }

    load() {
      /* un produit purgé pendant le chargement redéclenche l'événement : pas de réentrance */
      if (this.isLoading) return;

      var handles = Wishlist.all();
      if (handles.length === 0) {
        this.toggleEmpty(true);
        return;
      }

      this.isLoading = true;
      this.classList.add('is-loading');
      var self = this;

      Promise.all(
        handles.map(function (handle) {
          if (self.rendered.indexOf(handle) !== -1) return Promise.resolve(null);
          return fetch(self.rootUrl + '/products/' + encodeURIComponent(handle) + '?view=card')
            .then(function (response) {
              /* produit dépublié ou supprimé : on le purge de la liste */
              if (!response.ok) {
                Wishlist.remove(handle);
                return null;
              }
              return response.text();
            })
            .then(function (html) {
              return html ? { handle: handle, html: html } : null;
            })
            .catch(function () {
              return null;
            });
        })
      ).then(function (results) {
        results.forEach(function (result) {
          if (!result) return;
          var item = document.createElement('li');
          item.className = 'wishlist-grid__item';
          item.setAttribute('data-wishlist-item', result.handle);
          item.innerHTML = result.html;
          self.list.appendChild(item);
          self.rendered.push(result.handle);
        });
        self.isLoading = false;
        self.toggleEmpty(self.rendered.length === 0);
      });
    }
  }
  customElements.define('wishlist-grid', WishlistGrid);
})();
